/*
 * hp-product-reviews — the reviews block, built on Okendo's Storefront REST API.
 *
 * ── WHY THIS EXISTS RATHER THAN OKENDO'S WIDGET ────────────────────────────
 * The design (Figma 9120:8881) is not a variant of any Okendo embed: score and
 * histogram beside a recommend figure and a media rail, then rows whose stars
 * and date sit in their own left column. Their widget cannot be configured into
 * that shape, so the data comes from them and the markup is ours.
 *
 * ── THE API IS PUBLIC, AND THAT IS DOCUMENTED, NOT ASSUMED ────────────────
 * Okendo publish two REST surfaces. The Merchant API is server-side and gated
 * behind their Advanced plan; this is the STOREFRONT API, which their own docs
 * describe as "a public API that is intended to be used on a storefront or
 * other public client", with "No authentication is required" and an explicit
 * invitation to "build custom displays with your data". Nothing here needs a
 * key and nothing here is plan-gated.
 *
 *   base   https://api.okendo.io/v1/stores/{okendoUserId}
 *   GET  /products/shopify-{id}/review_aggregate   score, histogram, recommend
 *   GET  /products/shopify-{id}/reviews            the list
 *   GET  /products/shopify-{id}/review_media       the media rail
 *
 * ── FOUR THINGS THE DOCS AND THE MEASUREMENTS SETTLED ─────────────────────
 *
 * 1. `nextUrl` IS ROOT-RELATIVE AND OMITS THE `/v1` PREFIX. It comes back as
 *    "/stores/…/reviews?…", so fetching it verbatim resolves against the
 *    STOREFRONT and returns the theme's own 404 page — which parses as HTML and
 *    fails as JSON. It cost a round of "pagination is broken at every page
 *    size" before the cause turned out to be the URL, not the API. Okendo's
 *    docs say to "append the nextUrl to the base path"; API_ROOT is that path.
 *
 * 2. `limit` IS DOCUMENTED 1-25. Higher values are accepted (100 returns 100)
 *    but are outside the documented contract, so PAGE_SIZE stays inside it.
 *
 * 3. THE VERSION HEADER CANNOT BE SENT FROM A BROWSER. The docs require
 *    `okendo-api-version: 2025-02-01` on all requests. Sending it makes the
 *    request non-simple, the preflight comes back with no
 *    Access-Control-Allow-Headers, and the fetch is blocked outright — measured:
 *    with the header "Failed to fetch", without it 200. So on a storefront, the
 *    only place this API is meant to be used, their own requirement cannot be
 *    met. We call unversioned, which is what their widget effectively does.
 *    If a response shape ever changes under us, this is the reason.
 *
 * 4. THERE IS NO RATING FILTER, AND THAT IS CONFIRMED BY THE DOCS, not just
 *    inferred. The reviews endpoint's documented query params are `limit`
 *    (1-25), `orderBy` and `locale` — nothing else. `filter=rating:5` returns
 *    200 and is silently ignored (verified: rating:5 and rating:1 return the
 *    identical mixed set), which is the worst kind of missing feature: it looks
 *    like it worked.
 *
 *    So the star filter is applied CLIENT-SIDE, over pages fetched in the
 *    user's chosen order. See `collectFiltered` for how it stays bounded and
 *    why the counts it displays are trustworthy even though the filtering is
 *    not done by the server.
 *
 * ── LOADING IS LAZY, AND THE GUARANTEE IS A TIMER — NOT AN EVENT ─────────
 * The block sits well below the fold and most visitors never reach it, so
 * firing three requests on every product page view would be waste. It loads on
 * approach instead.
 *
 * WHAT IT DOES NOT TRUST IS ANY EVENT. IntersectionObserver never fires in the
 * browser pane this theme is verified in (CLAUDE.md), and measuring it here
 * found that SCROLL EVENTS DO NOT FIRE EITHER: a listener attached fresh to
 * both window and document caught zero events across a `window.scrollBy` that
 * visibly moved the page. So a scroll-triggered block sits empty forever in the
 * one environment we can check it in, which is the exact shape CLAUDE.md warns
 * about — "a guarantee conditional on a mechanism that can silently not run is
 * not a guarantee".
 *
 * So the load is armed by a plain `setInterval` rect check, which cannot
 * not-fire, and the scroll/resize listeners are kept only to make it feel
 * immediate when they do work. The poll is a `getBoundingClientRect` every
 * 500ms, it stops the moment the block loads, and it gives up after 60s so it
 * can never outlive the page's interest in it.
 */
(function () {
  const API_ROOT = 'https://api.okendo.io/v1';

  /* Documented: 1-25. See note 2 above. */
  const PAGE_SIZE = 25;

  /* How far below the viewport to start fetching, so the block is populated by
     the time it is scrolled to rather than filling in under the reader. */
  const LOAD_LEAD_PX = 800;

  /* The backstop that makes the load unconditional. See the header note. */
  const POLL_MS = 500;
  const POLL_GIVE_UP_MS = 60000;

  /* The node draws at most three thumbnails on a row (9120:9008). */
  const MAX_ROW_MEDIA = 3;

  /*
   * ── WHY ALL THE MEDIA IS FETCHED UP FRONT ────────────────────────────────
   * Media and reviews are SEPARATE endpoints with independent cursors, and the
   * reviews endpoint carries no media of its own — verified against `include`,
   * `expand`, `withMedia` and `media` params, none of which change the payload.
   * Each media item names its `reviewId`, so the row thumbnails are a join, and
   * a join needs the whole of one side.
   *
   * It is affordable because review media is RARE in this catalogue: 13 of 60
   * products have any at all, and the largest holds 37 items — two requests.
   * The cap exists for the product that one day holds hundreds; past it, later
   * rows simply render without thumbnails rather than the page issuing a
   * dozen requests nobody asked for.
   */
  const MEDIA_PAGE_CAP = 4;

  /*
   * How many pages one filtered batch may pull before giving up. Filtering is
   * client-side, so a rare rating can hide behind a lot of common ones: on
   * Glass Grinder the 17 one-star reviews are scattered through 152. Eight
   * pages of 25 is 200 reviews — more than any product in this catalogue holds
   * (the largest is 199), so in practice a filter always resolves completely.
   * The cap is what stops a future 2,000-review product issuing 80 requests.
   */
  const FILTER_PAGE_CAP = 8;

  /* `all` means no filter. Anything else is a star level as a number. */
  const NO_FILTER = 'all';

  /* Okendo's documented orderBy values, paired with the design's wording.
     `has_media desc` is the node's own "Photos & Videos" sort — and it is worth
     more than a label here: this catalogue has 17 media items across 125
     reviews, so sorted by date only 1 of the first 25 rows carries a photo,
     against 12 of 25 sorted by media. It is what makes the design's media-rich
     rows honest rather than aspirational. */
  const SORTS = [
    ['has_media desc', 'Photos & Videos'],
    ['date desc', 'Most Recent'],
    ['rating desc', 'Highest Rated'],
    ['rating asc', 'Lowest Rated'],
    ['helpful desc', 'Most Helpful']
  ];

  const STAR_COUNT = 5;

  /*
   * Review bodies, titles and display names are written by the public and
   * arrive as raw strings. They are interpolated into markup here, so every one
   * of them goes through this first. There is no rich text in the payload — the
   * API returns plain strings — so escaping loses nothing.
   */
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /*
   * ── A BRAND REPLY IS HTML, NOT A PLAIN STRING ───────────────────────────
   * Everything else in the payload is plain text and goes through `esc`. The
   * `reply.body` is the one exception: Okendo's reply editor stores markup —
   * measured across the 22 replies in the store's latest 750 reviews, `<p>`
   * paragraphs, the odd `<a>`, and entities such as `&#39;`. Escaping it would
   * print the tags as literal text, so it is sanitised instead.
   *
   * An allowlist, rebuilt into fresh elements rather than filtered in place:
   * anything not listed is UNWRAPPED (its text kept, the element dropped), so
   * an unexpected tag loses its formatting and never its words. Links keep only
   * an http(s)/mailto href. Parsing happens in a <template>, whose content is
   * inert — no script runs and no image loads while it is being read.
   */
  const REPLY_TAGS = ['P', 'BR', 'A', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI'];
  const REPLY_DROP = ['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'SVG', 'MATH'];

  function sanitiseReply(html) {
    const source = document.createElement('template');
    source.innerHTML = String(html || '');
    const out = document.createElement('div');

    (function copy(from, to) {
      from.childNodes.forEach(function (node) {
        if (node.nodeType === Node.TEXT_NODE) {
          to.appendChild(document.createTextNode(node.textContent));
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        /* Unwrapping these would print their source as words. */
        /* Upper-cased because foreign elements (svg, math) keep a lowercase
           tagName even in an HTML document. */
        if (REPLY_DROP.indexOf(node.tagName.toUpperCase()) !== -1) return;
        if (REPLY_TAGS.indexOf(node.tagName) === -1) { copy(node, to); return; }
        const el = document.createElement(node.tagName.toLowerCase());
        if (node.tagName === 'A') {
          const href = node.getAttribute('href') || '';
          if (!/^(https?:|mailto:)/i.test(href.trim())) { copy(node, to); return; }
          el.setAttribute('href', href.trim());
          el.setAttribute('rel', 'noopener');
        }
        copy(node, el);
        to.appendChild(el);
      });
    })(source.content, out);

    /* A body with no block wrapper at all still lands in a paragraph, so the
       spacing rules have one shape to style. */
    if (out.textContent.trim() && !out.querySelector('p, ul, ol')) {
      const p = document.createElement('p');
      while (out.firstChild) p.appendChild(out.firstChild);
      out.appendChild(p);
    }
    return out.textContent.trim() ? out.innerHTML : '';
  }

  /*
   * "4 Months Ago", "1 Year Ago" — the node's wording, title-cased, and the
   * largest whole unit rather than a precise interval. Anything under a month
   * still reads in weeks/days so a brand-new review does not say "0 Months".
   */
  const UNITS = [
    ['Year', 31536000],
    ['Month', 2592000],
    ['Week', 604800],
    ['Day', 86400]
  ];

  function relativeDate(iso) {
    const then = Date.parse(iso);
    if (!Number.isFinite(then)) return '';
    const seconds = Math.max(0, (Date.now() - then) / 1000);
    for (let i = 0; i < UNITS.length; i++) {
      const n = Math.floor(seconds / UNITS[i][1]);
      if (n >= 1) return n + ' ' + UNITS[i][0] + (n === 1 ? '' : 's') + ' Ago';
    }
    return 'Today';
  }

  /*
   * Initials for the avatar chip. Up to two words, so "Kyle F." gives KF and
   * "Chris" gives C. Falls back to a dash rather than an empty circle.
   */
  function initials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '-';
    return parts.slice(0, 2).map(function (p) { return p[0].toUpperCase(); }).join('');
  }

  /*
   * ── THIS IS THE SHARED `.hp-stars` COMPONENT, NOT A SECOND ONE ───────────
   * `snippets/hp-star-rating.liquid` and `.hp-stars` in custom.css already own
   * this construction — track + clipped fill, sized by `--hp-star-size` and
   * `--hp-star-gap` and filled by `--hp-stars-fill`, ALL THREE AS REM LENGTHS.
   * This function exists only because review rows are rendered in script and
   * cannot call a Liquid snippet; it must emit exactly what that snippet emits,
   * including `.hp-star` for the individual star.
   *
   * IT PREVIOUSLY DID NOT, AND IT BROKE EVERY PRODUCT CARD. An earlier version
   * shipped a parallel `.hp-stars` block reading `--hp-star-w` as a UNITLESS
   * number (`calc(var(--hp-star-gap, 4) * 0.1rem)`). The card passes
   * `--hp-star-gap: 0.4rem`, so that computed `calc(0.4rem * 0.1rem)` — a
   * length times a length, invalid, dropped at computed-value time — leaving
   * `gap: 0`. The row collapsed from 86px to 70px while the fill was still
   * computed for 86, so every rating above ~4 painted all five stars. It read
   * as "the ratings are wrong" and was actually one duplicated CSS block.
   *
   * THE FILL WIDTH IS NOT `fraction x 100%`. The stars sit on a gap, and that
   * gap is dead space the fill must cross in one step rather than bleed into:
   * whole stars each consume (star + gap), the remainder consumes part of the
   * next star's width only, and the total caps at the row's real width because
   * the last star carries no trailing gap. Identical maths to the snippet's —
   * if one changes, change both.
   */
  /*
   * ── EMITS TWO NUMBERS, NOT A WIDTH ───────────────────────────────────────
   * The rating's whole and fractional halves go out as unitless custom
   * properties and `.hp-stars__fill` in custom.css does the arithmetic against
   * whatever `--hp-star-size` is in force. Nothing about the size is set
   * inline, which is the ONLY way these rows can be 24px on desktop and 20 on
   * mobile: an inline custom property beats any stylesheet rule, so a resolved
   * length here would pin the fill to one breakpoint's geometry.
   *
   * It also means this file no longer carries a copy of the fill formula — it
   * lived here, in hp-star-rating.liquid and in custom.css, and three copies of
   * one piece of arithmetic is how they drift.
   *
   * `.hp-star` IS THE SHARED COMPONENT'S ELEMENT, and the SVG must arrive with
   * its hardcoded hex already swapped for currentColor (the section does it).
   * The row draws the same markup twice — pale track under gold fill — so a
   * baked-in fill paints both layers gold and every rating looks like five.
   */
  function starsMarkup(value, starSvg) {
    const clamped = Math.max(0, Math.min(STAR_COUNT, Number(value) || 0));
    const whole = Math.floor(clamped);
    const part = +(clamped - whole).toFixed(4);
    const row = new Array(STAR_COUNT + 1).join('<span class="hp-star">' + starSvg + '</span>');
    return (
      '<span class="hp-stars" style="--hp-stars-whole:' + whole + ';--hp-stars-part:' + part + '">' +
        '<span class="hp-stars__track">' + row + '</span>' +
        '<span class="hp-stars__fill">' + row + '</span>' +
      '</span>'
    );
  }

  class HpProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.__hpInit) return;
      this.__hpInit = true;

      this.userId = this.dataset.okendoUserId;
      this.productId = this.dataset.productId;
      this.sort = this.dataset.sort || SORTS[0][0];
      this.starSvg = this.dataset.starSvg || '';

      this.els = {
        histogram: this.querySelector('[data-hp-reviews-histogram]'),
        recommend: this.querySelector('[data-hp-reviews-recommend]'),
        media: this.querySelector('[data-hp-reviews-media]'),
        list: this.querySelector('[data-hp-reviews-list]'),
        more: this.querySelector('[data-hp-reviews-more]'),
        count: this.querySelector('[data-hp-reviews-tabcount]'),
        status: this.querySelector('[data-hp-reviews-status]')
      };

      /* Without an id there is nothing to call. Say so in the console rather
         than failing silently with an empty block on the page. */
      if (!this.userId || !this.productId) {
        /* eslint-disable-next-line no-console */
        console.warn('[hp-product-reviews] missing Okendo user id or product id; block will not load');
        return;
      }

      this.reviews = [];
      this.nextUrl = null;
      this.loading = false;
      this.rating = NO_FILTER;
      this.byLevel = {};

      this.bindSort();
      this.bindFilter();
      this.bindMore();
      this.bindMedia();

      this.onScroll = this.maybeLoad.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.pollStarted = Date.now();
      this.poll = setInterval(this.maybeLoad.bind(this), POLL_MS);

      this.maybeLoad();
    }

    disconnectedCallback() {
      this.stopWatching();
      this.teardownLightbox();
    }

    stopWatching() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.poll) { clearInterval(this.poll); this.poll = null; }
    }

    /* The lightbox lives on <body>, so it outlives this element unless it is
       taken down explicitly — and its keydown listener is on the document. */
    teardownLightbox() {
      if (this.onLightboxKey) document.removeEventListener('keydown', this.onLightboxKey);
      document.documentElement.classList.remove('hp-lightbox-open');
      if (this.lightbox && this.lightbox.parentNode) this.lightbox.parentNode.removeChild(this.lightbox);
      this.lightbox = null;
    }

    maybeLoad() {
      if (this.loaded || this.loading) return;
      if (Date.now() - this.pollStarted > POLL_GIVE_UP_MS) { this.stopWatching(); return; }
      const rect = this.getBoundingClientRect();
      if (rect.top > window.innerHeight + LOAD_LEAD_PX) return;
      this.loaded = true;
      this.stopWatching();
      this.load();
    }

    base() {
      return API_ROOT + '/stores/' + encodeURIComponent(this.userId) +
             '/products/shopify-' + encodeURIComponent(this.productId);
    }

    async getJson(url) {
      const response = await fetch(url);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    }

    /*
     * The three calls go out together rather than in sequence: they are
     * independent, and in parallel the block costs ONE round of latency
     * (measured ~200ms) instead of three.
     */
    async load() {
      this.loading = true;
      this.setBusy(true);
      try {
        const results = await Promise.all([
          this.getJson(this.base() + '/review_aggregate'),
          this.getJson(this.listUrl()),
          this.loadAllMedia()
        ]);
        this.renderAggregate(results[0].reviewAggregate || {});
        /* No filter is active on first load, so this page is used as-is rather
           than re-walked. */
        this.reviews = results[1].reviews || [];
        this.nextUrl = results[1].nextUrl || null;
        this.indexMedia(results[2]);
        this.renderMedia();
        this.renderList();
      } catch (error) {
        this.fail(error);
      } finally {
        this.loading = false;
        this.setBusy(false);
      }
    }

    /*
     * ── THE CLIENT-SIDE FILTER ────────────────────────────────────────────
     * Pulls pages in the CURRENT SORT ORDER and keeps only the matching stars,
     * until it has a full page of them, runs out of reviews, or hits the cap.
     * Server order is preserved exactly — the filter removes rows, it never
     * reorders them — so "Highest Rated" still means what it says while a
     * filter is on.
     *
     * The alternative was to fetch everything once and filter in memory. It
     * would make later interactions instant, but it re-implements the server's
     * ordering client-side, and two orderings that disagree on ties is a bug
     * nobody would think to look for. Paging keeps one source of truth.
     *
     * Returns the reviews collected and leaves `nextUrl` pointing at wherever
     * it stopped, so View More resumes the same walk rather than restarting.
     */
    async collectFiltered(startUrl, want) {
      const collected = [];
      let url = startUrl;
      let pages = 0;
      while (url && pages < FILTER_PAGE_CAP && collected.length < want) {
        const data = await this.getJson(url);
        (data.reviews || []).forEach((review) => {
          if (this.matchesFilter(review)) collected.push(review);
        });
        pages++;
        const next = data.nextUrl;
        url = next ? (/^https?:\/\//.test(next) ? next : API_ROOT + next) : null;
      }
      this.nextUrl = url;
      /* Hit the cap with more to come: say so rather than letting an incomplete
         list look complete. Cleared as soon as a batch finishes cleanly. */
      this.filterTruncated = !!url && pages >= FILTER_PAGE_CAP;
      return collected;
    }

    matchesFilter(review) {
      if (this.rating === NO_FILTER) return true;
      return Number(review.rating) === Number(this.rating);
    }

    listUrl() {
      return this.base() + '/reviews?limit=' + PAGE_SIZE +
             '&orderBy=' + encodeURIComponent(this.sort);
    }

    /*
     * A sort change re-fetches page one; it does not sort what is already here.
     * Only the first page is in memory, so a client-side sort would reorder 25
     * of 125 reviews and present it as the whole ordering.
     */
    async reload() {
      this.loading = true;
      this.setBusy(true);
      try {
        this.reviews = await this.collectFiltered(this.listUrl(), PAGE_SIZE);
        this.renderList();
      } catch (error) {
        this.fail(error);
      } finally {
        this.loading = false;
        this.setBusy(false);
      }
    }

    async loadMore() {
      if (!this.nextUrl || this.loading) return;
      this.loading = true;
      this.setBusy(true);
      try {
        /* See note 1 in the file header: nextUrl is root-relative AND omits the
           /v1 prefix, so it is resolved against API_ROOT, never the storefront.
           `collectFiltered` normalises it again for the pages after this one. */
        const url = /^https?:\/\//.test(this.nextUrl) ? this.nextUrl : API_ROOT + this.nextUrl;
        const more = await this.collectFiltered(url, PAGE_SIZE);
        this.reviews = this.reviews.concat(more);
        this.renderList();
      } catch (error) {
        this.fail(error);
      } finally {
        this.loading = false;
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.toggleAttribute('data-hp-reviews-busy', busy);
      if (this.els.status) {
        this.els.status.textContent = busy ? 'Loading reviews' : '';
      }
    }

    /*
     * A failed fetch leaves whatever is already rendered in place and says so
     * once, rather than blanking a block that may already hold reviews.
     */
    fail(error) {
      /* eslint-disable-next-line no-console */
      console.warn('[hp-product-reviews]', error);
      this.setAttribute('data-hp-reviews-error', '');
      if (this.els.list && !this.reviews.length) {
        this.els.list.innerHTML =
          '<p class="hp-reviews__failed hp-text-body-l-reg">Reviews could not be loaded right now.</p>';
      }
    }

    /*
     * Pages through the media endpoint until it runs out or the cap is hit.
     * Images AND video. The two carry their URLs under DIFFERENT KEYS —
     * `imageUrls` vs `videoUrls` — so a reader that only knows about the first
     * silently drops the second: this catalogue holds exactly one customer
     * video, which is precisely the sort of single item that goes unnoticed
     * until the person who filmed it asks where it went. Anything else (a type
     * they add later) is skipped rather than guessed at.
     */
    async loadAllMedia() {
      const all = [];
      let url = this.base() + '/review_media?limit=' + PAGE_SIZE;
      for (let page = 0; page < MEDIA_PAGE_CAP && url; page++) {
        let data;
        try {
          data = await this.getJson(url);
        } catch (error) {
          /* Media is decoration. A failure here must not take the reviews with
             it, so it resolves with whatever it managed to collect. */
          break;
        }
        (data.media || []).forEach(function (item) {
          if (item && (item.type === 'image' || item.type === 'video')) all.push(item);
        });
        const next = data.nextUrl;
        /* Same relative-URL rule as the review cursor — see note 1 up top. */
        url = next ? (/^https?:\/\//.test(next) ? next : API_ROOT + next) : null;
      }
      return all;
    }

    /*
     * One flat ordered list for the lightbox to roam, plus a reviewId -> items
     * map for the row thumbnails. Both views are the same array, so an index in
     * the lightbox always refers to the same picture the reader clicked.
     */
    indexMedia(media) {
      this.allMedia = media || [];
      this.mediaByReview = {};
      this.allMedia.forEach((item, index) => {
        item.__hpIndex = index;
        const id = item.reviewId;
        if (!id) return;
        (this.mediaByReview[id] = this.mediaByReview[id] || []).push(item);
      });
    }

    renderAggregate(aggregate) {
      const total = Number(aggregate.reviewCount) || 0;
      const byLevel = aggregate.reviewCountByLevel || aggregate.ratingAndReviewCountByLevel || {};
      this.byLevel = byLevel;
      this.reviewTotal = total;

      if (this.els.count) this.els.count.textContent = String(total);
      this.labelFilterOptions();

      if (this.els.histogram) {
        let rows = '';
        for (let level = STAR_COUNT; level >= 1; level--) {
          const count = Number(byLevel['level' + level + 'Count']) || 0;
          /* Bars are a share of the TOTAL, not of the largest bar. Scaling to
             the largest would draw a full-width bar for the top rating on every
             product, which says nothing about how the ratings are distributed. */
          const pct = total ? (count / total) * 100 : 0;
          rows +=
            '<li class="hp-reviews__hist-row">' +
              '<span class="hp-reviews__hist-level hp-text-body-l-reg">' + level + '</span>' +
              '<span class="hp-reviews__hist-star" aria-hidden="true">' + this.starSvg + '</span>' +
              '<span class="hp-reviews__hist-track">' +
                '<span class="hp-reviews__hist-fill" style="inline-size:' + pct.toFixed(2) + '%"></span>' +
              '</span>' +
              '<span class="hp-reviews__hist-count hp-text-body-l-reg">' + count + '</span>' +
            '</li>';
        }
        this.els.histogram.innerHTML =
          '<ul class="hp-reviews__hist" role="list">' + rows + '</ul>';
      }

      if (this.els.recommend) {
        const recommended = Number(aggregate.recommendationCount);
        /* Drawn only when the figure is real. A product nobody answered the
           recommend question on would otherwise show a confident 0%. */
        if (total > 0 && Number.isFinite(recommended) && recommended > 0) {
          const pct = Math.round((recommended / total) * 100);
          this.els.recommend.innerHTML =
            '<p class="hp-reviews__recommend-value hp-text-heading-h1">' + pct + '%</p>' +
            '<p class="hp-reviews__recommend-label hp-text-body-reg">Of Customers Would Recommend This Product</p>';
        } else {
          this.els.recommend.innerHTML = '';
        }
      }
    }

    /*
     * A thumbnail is a BUTTON, not an image in a link. It opens a viewer on the
     * same page rather than navigating, so a link would be lying about what the
     * click does — and a bare <img> with a handler is unreachable by keyboard.
     */
    /* The only place that knows a media item has two possible URL shapes. */
    static poster(item) {
      const urls = item.type === 'video' ? (item.videoUrls || {}) : (item.imageUrls || {});
      return urls.videoPosterUrl || urls.thumbnailUrl || urls.largeUrl || urls.fullSizeUrl || '';
    }

    static full(item) {
      const urls = item.type === 'video' ? (item.videoUrls || {}) : (item.imageUrls || {});
      return urls.fullSizeUrl || urls.largeUrl || urls.thumbnailUrl || '';
    }

    thumbMarkup(item, className) {
      const src = HpProductReviews.poster(item);
      if (!src) return '';
      const isVideo = item.type === 'video';
      /* The alt text is Okendo's own generated description. Where it is missing
         the image is decorative-by-default rather than announced as an unnamed
         button, so the label falls back to describing the action. */
      const alt = item.imageAlt || '';
      return (
        '<button type="button" class="' + className + (isVideo ? ' is-video' : '') +
          '" data-hp-media-index="' + item.__hpIndex + '">' +
          '<img class="hp-reviews__media-img" src="' + esc(src) + '" alt="' + esc(alt) +
          '" loading="lazy" decoding="async" width="142" height="142">' +
          (isVideo
            ? '<span class="hp-reviews__media-play" aria-hidden="true">' + (this.dataset.playSvg || '') + '</span>'
            : '') +
          '<span class="visually-hidden">' +
            (isVideo ? 'Play video' : 'Open photo') + (alt ? ': ' + esc(alt) : '') +
          '</span>' +
        '</button>'
      );
    }

    renderMedia() {
      if (!this.els.media) return;
      const media = this.allMedia || [];
      if (!media.length) { this.els.media.innerHTML = ''; return; }
      const items = media.map((item) => {
        const thumb = this.thumbMarkup(item, 'hp-reviews__media-thumb');
        return thumb ? '<li class="hp-reviews__media-item">' + thumb + '</li>' : '';
      }).join('');
      this.els.media.innerHTML = '<ul class="hp-reviews__media-rail" role="list">' + items + '</ul>';
    }

    renderList() {
      if (!this.els.list) return;
      this.els.list.innerHTML = this.reviews.length
        ? this.reviews.map(this.reviewMarkup, this).join('')
        : '<li class="hp-reviews__none hp-text-body-l-reg">No reviews match this rating.</li>';
      if (this.els.more) {
        this.els.more.hidden = !this.nextUrl;
      }
      this.renderFilterStatus();
    }

    reviewMarkup(review) {
      const reviewer = review.reviewer || {};
      const name = reviewer.displayName || 'Anonymous';
      const date = relativeDate(review.dateCreated);

      /*
       * "Verified Buyer" is drawn ONLY on an explicit `isVerified: true`.
       * 35 of 100 sampled reviews carry no such field at all (they arrive with
       * `externalProvider: "shopify-shop"` instead), and inferring verification
       * from an import source would put a trust badge on reviews Okendo itself
       * does not call verified. Absent means absent.
       */
      /*
       * Up to three, which is what the node draws (9120:9008). Anything beyond
       * is reachable from the viewer, which roams the whole product's media —
       * so nothing is hidden, it just is not all on the row.
       */
      const mine = (this.mediaByReview && this.mediaByReview[review.reviewId]) || [];
      const rowMedia = mine.length
        ? '<div class="hp-reviews__row-media">' +
            mine.slice(0, MAX_ROW_MEDIA)
              .map((item) => this.thumbMarkup(item, 'hp-reviews__row-thumb'), this)
              .join('') +
          '</div>'
        : '';

      const verified = reviewer.isVerified === true
        ? '<span class="hp-reviews__verified">' +
            '<span class="hp-reviews__verified-text hp-text-body-reg">Verified Buyer</span>' +
            '<span class="hp-reviews__verified-badge" aria-hidden="true">' +
              (this.dataset.badgeSvg || '') +
            '</span>' +
          '</span>'
        : '';

      /*
       * `__row-head` pairs the reviewer with the stars/date. The two nodes
       * disagree about where the meta goes — desktop gives it its own left
       * column beside the copy (9120:8982), mobile sits it at the trailing end
       * of the reviewer row (9120:10242) — and no amount of CSS ordering can
       * pair elements that live in different parents. So the wrapper exists
       * always and DISSOLVES on desktop with `display: contents`, letting both
       * become items of the row's grid.
       */
      return (
        '<li class="hp-reviews__row">' +
          '<div class="hp-reviews__row-head">' +
            '<div class="hp-reviews__reviewer">' +
              '<span class="hp-reviews__avatar" aria-hidden="true">' +
                '<span class="hp-reviews__avatar-initials hp-text-body-bold">' +
                  esc(initials(name)) +
                '</span>' +
              '</span>' +
              '<span class="hp-reviews__reviewer-id">' +
                '<span class="hp-reviews__reviewer-name hp-text-body-l-bold">' + esc(name) + '</span>' +
                verified +
              '</span>' +
            '</div>' +
            '<div class="hp-reviews__row-meta">' +
              starsMarkup(review.rating, this.starSvg) +
              '<span class="visually-hidden">Rated ' + esc(review.rating) + ' out of 5</span>' +
              (date ? '<p class="hp-reviews__row-date hp-text-body-l-reg">' + esc(date) + '</p>' : '') +
            '</div>' +
          '</div>' +
          '<div class="hp-reviews__body">' +
            (review.title
              ? '<h3 class="hp-reviews__title hp-text-editorial-h4">' + esc(review.title) + '</h3>'
              : '') +
            (review.body
              ? '<p class="hp-reviews__text hp-text-body-l-reg">' + esc(review.body) + '</p>'
              : '') +
          '</div>' +
          rowMedia +
          this.replyMarkup(review.reply) +
        '</li>'
      );
    }

    /*
     * ── THE BRAND'S REPLY. NO FIGMA NODE — PROVISIONAL ───────────────────────
     * Okendo returns it on the review as `reply: { title, body, dateCreated,
     * avatarUrl }`; `title` is the sender name set in Okendo's admin. The
     * treatment is a judgement call built from the theme's own vocabulary: the
     * Cream 100 / Dark Brown panel pairing `.hp-sub` uses on this page, under
     * the review it answers, with the reply's avatar standing in for the
     * reviewer's initials disc.
     *
     * Rendered only when the body has words in it, so an empty or stripped
     * reply draws nothing rather than an empty panel.
     */
    replyMarkup(reply) {
      if (!reply) return '';
      const body = sanitiseReply(reply.body);
      if (!body) return '';
      const name = String(reply.title || '').trim() || 'Houseplant';
      const date = relativeDate(reply.dateCreated);
      const avatar = reply.avatarUrl
        ? '<img class="hp-reviews__reply-avatar" src="' + esc(reply.avatarUrl) +
            '" alt="" width="32" height="32" loading="lazy" decoding="async">'
        : '';
      return (
        '<div class="hp-reviews__reply">' +
          '<div class="hp-reviews__reply-head">' +
            avatar +
            '<p class="hp-reviews__reply-by hp-text-body-bold">Reply from ' + esc(name) + '</p>' +
            (date ? '<p class="hp-reviews__reply-date hp-text-body-reg">' + esc(date) + '</p>' : '') +
          '</div>' +
          '<div class="hp-reviews__reply-text hp-text-body-l-reg">' + body + '</div>' +
        '</div>'
      );
    }

    /* ── Lightbox ──────────────────────────────────────────────────────────
     * Built once, on first open, and appended to document.body — NOT left
     * inside the section. An absolutely positioned child is only clipped as far
     * as its containing block, and this one has to cover the viewport while the
     * media rail it was opened from is itself an `overflow-x: auto` scroller
     * (CLAUDE.md's escaping-a-scroll-container note, applied deliberately
     * rather than tripped over).
     */
    ensureLightbox() {
      if (this.lightbox) return this.lightbox;

      const box = document.createElement('div');
      box.className = 'hp-lightbox';
      box.hidden = true;
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', 'Customer photos');
      /*
       * `.ignore-empty` ON THE SCRIM AND THE STAGE IS LOAD-BEARING, NOT TIDINESS.
       * base.css hides every empty block element —
       * `div:empty:not(.ignore-empty) { display: none }` — and the scrim is
       * empty BY DESIGN, permanently. Without the class it computes to
       * `display: none` while still reporting the right colour and cursor, so
       * the viewer opens with no dimming at all and the pictures float over a
       * live page. The stage needs it too: it is emptied on close to stop any
       * playing video, which makes it empty at exactly the moment before the
       * next open fills it.
       *
       * The close glyph is the theme's shared one, drawn the way every other
       * overlay draws it: an empty span masked off `--hp-icon-close` with
       * `background-color: currentColor`, matching the cart drawer and the
       * search panel exactly (see section-hp-cart.css / section-hp-search.css).
       * Masking rather than inlining is what lets it inherit the button's
       * colour, and it keeps one file as the source of the glyph.
       */
      box.innerHTML =
        '<div class="hp-overlay hp-lightbox__scrim ignore-empty" data-hp-lightbox-close></div>' +
        '<div class="hp-lightbox__panel">' +
          '<button type="button" class="hp-lightbox__close" data-hp-lightbox-close>' +
            '<span class="hp-lightbox__close-glyph ignore-empty" aria-hidden="true"></span>' +
            '<span class="visually-hidden">Close</span>' +
          '</button>' +
          '<button type="button" class="hp-lightbox__nav hp-lightbox__nav--prev" data-hp-lightbox-step="-1">' +
            (this.dataset.arrowSvg || '') +
            '<span class="visually-hidden">Previous photo</span>' +
          '</button>' +
          '<figure class="hp-lightbox__figure">' +
            '<div class="hp-lightbox__stage ignore-empty" data-hp-lightbox-stage></div>' +
            '<figcaption class="hp-lightbox__caption" data-hp-lightbox-caption></figcaption>' +
          '</figure>' +
          '<button type="button" class="hp-lightbox__nav hp-lightbox__nav--next" data-hp-lightbox-step="1">' +
            (this.dataset.arrowSvg || '') +
            '<span class="visually-hidden">Next photo</span>' +
          '</button>' +
        '</div>';

      box.addEventListener('click', (event) => {
        if (event.target.closest('[data-hp-lightbox-close]')) { this.closeLightbox(); return; }
        const step = event.target.closest('[data-hp-lightbox-step]');
        if (step) this.showMedia(this.mediaIndex + Number(step.dataset.hpLightboxStep));
      });

      /* Keys are bound on the DOCUMENT rather than the dialog: the dialog only
         has focus while something inside it is focused, and clicking the scrim
         to look at the picture would otherwise take the arrows away. */
      this.onLightboxKey = (event) => {
        if (this.lightbox.hidden) return;
        if (event.key === 'Escape') { event.preventDefault(); this.closeLightbox(); }
        else if (event.key === 'ArrowLeft') { event.preventDefault(); this.showMedia(this.mediaIndex - 1); }
        else if (event.key === 'ArrowRight') { event.preventDefault(); this.showMedia(this.mediaIndex + 1); }
      };
      document.addEventListener('keydown', this.onLightboxKey);

      document.body.appendChild(box);
      this.lightbox = box;
      return box;
    }

    openLightbox(index) {
      if (!this.allMedia || !this.allMedia.length) return;
      const box = this.ensureLightbox();
      /* Remembered so focus can go back where it came from on close — a viewer
         that dumps the reader at the top of the document is worse than none. */
      this.lightboxOpener = document.activeElement;
      box.hidden = false;
      document.documentElement.classList.add('hp-lightbox-open');
      this.showMedia(index);
      const close = box.querySelector('.hp-lightbox__close');
      if (typeof trapFocus === 'function') trapFocus(box, close);
      else if (close) close.focus();
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      /* Emptying the stage stops any playing video; `hidden` alone would leave
         the audio running behind a closed viewer. */
      const stage = this.lightbox.querySelector('[data-hp-lightbox-stage]');
      if (stage) stage.innerHTML = '';
      this.lightbox.hidden = true;
      document.documentElement.classList.remove('hp-lightbox-open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.lightboxOpener);
      else if (this.lightboxOpener && this.lightboxOpener.focus) this.lightboxOpener.focus();
      this.lightboxOpener = null;
    }

    showMedia(index) {
      const media = this.allMedia || [];
      if (!media.length) return;
      /* Wraps rather than stopping at the ends: with the arrows always present,
         a dead button at each end reads as broken rather than as a boundary. */
      const count = media.length;
      this.mediaIndex = ((index % count) + count) % count;
      const item = media[this.mediaIndex];

      /* The stage is REPLACED per item rather than reused, so stepping off a
         video tears the <video> down instead of leaving it playing under the
         next picture. */
      const stage = this.lightbox.querySelector('[data-hp-lightbox-stage]');
      const src = HpProductReviews.full(item);
      stage.innerHTML = item.type === 'video'
        ? '<video class="hp-lightbox__video" controls playsinline preload="metadata" ' +
            'poster="' + esc(HpProductReviews.poster(item)) + '" src="' + esc(src) + '"></video>'
        : '<img class="hp-lightbox__img" src="' + esc(src) + '" alt="' + esc(item.imageAlt || '') + '">';

      /* The review that owns this photo, so the picture is not stranded from
         the words it belongs to. It is looked up rather than stored on the
         media item because the reviews list grows with View More. */
      const owner = (this.reviews || []).filter(function (r) {
        return r.reviewId === item.reviewId;
      })[0];
      const caption = this.lightbox.querySelector('[data-hp-lightbox-caption]');
      const position = '<span class="hp-lightbox__count hp-text-body-reg">' +
        (this.mediaIndex + 1) + ' of ' + count + '</span>';
      caption.innerHTML = owner
        ? '<span class="hp-lightbox__by">' +
            starsMarkup(owner.rating, this.starSvg) +
            '<span class="hp-lightbox__name hp-text-body-l-bold">' +
              esc((owner.reviewer && owner.reviewer.displayName) || 'Anonymous') +
            '</span>' +
          '</span>' +
          (owner.title ? '<span class="hp-lightbox__title hp-text-body-l-reg">' + esc(owner.title) + '</span>' : '') +
          position
        : position;

      /* Decode the neighbours so stepping does not flash an empty frame. */
      [this.mediaIndex - 1, this.mediaIndex + 1].forEach((i) => {
        const n = media[((i % count) + count) % count];
        /* Images only — pulling a neighbouring video's bytes to avoid a flash
           that lasts a frame is not a trade worth making. */
        if (n.type === 'video') return;
        const u = (n.imageUrls || {}).largeUrl;
        if (u) { const pre = new Image(); pre.src = u; }
      });
    }

    bindMedia() {
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-hp-media-index]');
        if (!thumb) return;
        event.preventDefault();
        this.openLightbox(Number(thumb.dataset.hpMediaIndex) || 0);
      });
    }

    /*
     * THE COUNTS COME FROM THE AGGREGATE, NOT FROM WHAT HAS BEEN FETCHED.
     * That distinction is what makes a client-side filter honest: the control
     * can say "1 star (17)" on a product where only 25 reviews have been
     * downloaded, because the server already counted them. A count derived from
     * the fetched set would start wrong and creep towards right as the reader
     * pressed View More, which is worse than no number at all.
     *
     * A level with no reviews is disabled rather than hidden — the row of
     * options is a distribution, and a missing 2-star tells the reader less
     * than a greyed one showing zero.
     */
    labelFilterOptions() {
      const dropdown = this.querySelector('.hp-reviews__filter');
      if (!dropdown) return;
      dropdown.querySelectorAll('[data-hp-filter-value]').forEach((button) => {
        const value = button.dataset.hpFilterValue;
        const countEl = button.querySelector('[data-hp-filter-count]');
        if (!countEl) return;
        if (value === NO_FILTER) {
          countEl.textContent = this.reviewTotal ? '(' + this.reviewTotal + ')' : '';
          return;
        }
        const n = Number(this.byLevel['level' + value + 'Count']) || 0;
        countEl.textContent = '(' + n + ')';
        button.disabled = n === 0;
      });
    }

    filterLabel(value) {
      if (value === NO_FILTER) return 'All Ratings';
      return value + ' Star' + (Number(value) === 1 ? '' : 's');
    }

    bindFilter() {
      const dropdown = this.querySelector('.hp-reviews__filter');
      if (!dropdown) return;
      const valueEl = dropdown.querySelector('[data-hp-dropdown-value]');
      dropdown.addEventListener('click', (event) => {
        const button = event.target.closest('[data-hp-filter-value]');
        if (!button || button.disabled) return;
        const value = button.dataset.hpFilterValue;
        dropdown.removeAttribute('open');
        if (value === this.rating) return;
        this.rating = value;
        if (valueEl) valueEl.textContent = this.filterLabel(value);
        dropdown.querySelectorAll('[data-hp-filter-value]').forEach((b) => {
          b.setAttribute('aria-pressed', String(b.dataset.hpFilterValue === value));
        });
        this.reload();
      });
    }

    /*
     * Said out loud only when a filter is on. With no filter the tab already
     * carries the total and a second count beside it would just be noise.
     */
    renderFilterStatus() {
      const el = this.querySelector('[data-hp-reviews-filterstatus]');
      if (!el) return;
      if (this.rating === NO_FILTER) { el.textContent = ''; el.hidden = true; return; }
      const total = Number(this.byLevel['level' + this.rating + 'Count']) || 0;
      const shown = this.reviews.length;
      el.hidden = false;
      el.textContent = this.filterTruncated
        ? 'Showing ' + shown + ' of ' + total + ' — refine the sort to reach the rest'
        : 'Showing ' + shown + ' of ' + total;
    }

    bindSort() {
      /* hp-dropdown takes a `class` but no arbitrary attributes, so the control
         is found by that class rather than a data hook it cannot emit. */
      const dropdown = this.querySelector('.hp-reviews__sort');
      if (!dropdown) return;
      const valueEl = dropdown.querySelector('[data-hp-dropdown-value]');
      dropdown.addEventListener('click', (event) => {
        const button = event.target.closest('[data-hp-sort-value]');
        if (!button) return;
        const value = button.dataset.hpSortValue;
        if (!value || value === this.sort) { dropdown.removeAttribute('open'); return; }
        this.sort = value;
        const match = SORTS.filter(function (s) { return s[0] === value; })[0];
        if (valueEl && match) valueEl.textContent = match[1];
        dropdown.querySelectorAll('[data-hp-sort-value]').forEach(function (b) {
          b.setAttribute('aria-pressed', String(b.dataset.hpSortValue === value));
        });
        dropdown.removeAttribute('open');
        this.reload();
      });
    }

    bindMore() {
      const button = this.querySelector('[data-hp-reviews-more-button]');
      if (!button) return;
      button.addEventListener('click', () => { this.loadMore(); });
    }
  }

  if (!customElements.get('hp-product-reviews')) {
    customElements.define('hp-product-reviews', HpProductReviews);
  }
})();
